import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import PhoneInput from './shared/PhoneInput';
import { Input } from '../components/ui/input';
import { PAGE_PAUSED_COPY, isLinkPaused } from './errors';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PAGE_BG = '#faf7f2';
const INPUT = 'w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm';
// Same limits the server enforces on a public submit (ticket 303), so a person cannot type past them.
const FIELD_MAX = { first_name: 120, last_name: 120, email: 254, phone: 32 };
export const maxLengthFor = (key) => (Object.hasOwn(FIELD_MAX, key) ? FIELD_MAX[key] : 2000);
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid'];

const Notice = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center px-6 text-center" style={{ background: PAGE_BG }} data-testid="public-page-notice">
    <div className="text-slate-500">{children}</div>
  </div>
);

/* PUBLIC hosted lead form at /f/:slug — the same form the embed draws, on its own page.
   Posts to /api/leadgen/submit/:slug, so assignment, dedupe and attribution are identical. */
export default function LeadFormPublicPage() {
  const { slug } = useParams();
  const [query] = useSearchParams();
  const [schema, setSchema] = useState(null);
  const [err, setErr] = useState(null);
  const [values, setValues] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND}/api/leadgen/forms/${encodeURIComponent(slug)}`)
      .then(r => setSchema(r.data))
      .catch((e) => setErr(e?.response?.status === 404
        ? "This form isn't available."
        : isLinkPaused(e) ? PAGE_PAUSED_COPY
        : "We couldn't load this form. Check your connection and refresh to try again."));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    const body = {};
    UTM_KEYS.forEach(k => { if (query.get(k)) body[k] = query.get(k); });
    // origin + path only: the query string can carry tokens that are none of our business
    body.referrer = window.location.origin + window.location.pathname;
    for (const f of schema.fields || []) {
      const v = (values[f.key] || '').trim();
      if (f.required && !v) { toast({ title: `${f.label} is required` }); return; }
      if (v) body[f.key] = v;
    }
    setSending(true);
    try { await axios.post(`${BACKEND}/api/leadgen/submit/${encodeURIComponent(slug)}`, body); setDone(true); }
    catch (e) {
      if (isLinkPaused(e)) toast({ title: 'Submission failed', description: PAGE_PAUSED_COPY });
      else toast({ title: 'Submission failed', description: 'Please try again in a moment.' });
    }
    finally { setSending(false); }
  };

  if (err) return <Notice>{err}</Notice>;
  if (!schema) return <div className="min-h-screen flex items-center justify-center" style={{ background: PAGE_BG }}><Loader2 className="animate-spin" /></div>;

  const heading = schema.landing_headline || schema.headline || schema.name;
  return (
    <div className="min-h-screen" style={{ background: PAGE_BG }} data-testid="lead-form-page">
      <div className="max-w-xl mx-auto px-6 py-16">
        <div className="text-center">
          <h1 className="font-serif text-3xl md:text-4xl font-bold" style={{ color: '#070B5F' }} data-testid="lead-form-heading">{heading}</h1>
          {schema.subheadline && <p className="text-lg text-slate-600 mt-3">{schema.subheadline}</p>}
          {schema.landing_body && <p className="text-sm text-slate-600 mt-3 whitespace-pre-line" data-testid="lead-form-body">{schema.landing_body}</p>}
        </div>
        <div className="mt-8 bg-white rounded-2xl p-6 border border-slate-100 shadow">
          {done ? (
            <div className="text-center py-6" data-testid="lead-form-done">
              <CheckCircle2 size={48} className="mx-auto mb-3" style={{ color: '#6BA82E' }} />
              <div className="font-serif text-xl font-bold" style={{ color: '#070B5F' }}>Thanks, we received your details.</div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" noValidate>
              {(schema.fields || []).map(f => {
                const id = `lf-${f.key}`;
                return (
                  <div key={f.key}>
                    <label htmlFor={id} className="block text-xs font-medium text-slate-600 mb-1">{f.label}{f.required ? ' *' : ''}</label>
                    {f.type === 'tel' ? (
                      <PhoneInput id={id} value={values[f.key] || ''} onChange={(d) => setValues(v => ({ ...v, [f.key]: d }))}
                        className={`${INPUT} h-auto`} data-testid={`lead-field-${f.key}`} />
                    ) : f.type === 'textarea' ? (
                      <textarea id={id} rows={3} maxLength={maxLengthFor(f.key)} value={values[f.key] || ''} onChange={(e) => setValues(v => ({ ...v, [f.key]: e.target.value }))}
                        className={INPUT} data-testid={`lead-field-${f.key}`} />
                    ) : (
                      <Input id={id} type={f.type === 'email' ? 'email' : 'text'} maxLength={maxLengthFor(f.key)} autoComplete={f.type === 'email' ? 'email' : undefined}
                        value={values[f.key] || ''} onChange={(e) => setValues(v => ({ ...v, [f.key]: e.target.value }))}
                        className={`${INPUT} h-auto`} data-testid={`lead-field-${f.key}`} />
                    )}
                  </div>
                );
              })}
              <button type="submit" disabled={sending} className="w-full py-3 rounded-lg font-semibold text-white text-sm disabled:opacity-60" style={{ background: '#6BA82E' }} data-testid="lead-form-submit">
                {sending ? 'Sending…' : 'Submit'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
